import React, { useState } from 'react';
import { Card, Descriptions, Tag, Typography, Avatar, Input, Button, Space } from 'antd';
import {
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SaveOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { saveWbToken, deleteWbToken } from '../store/slices/userSlice';
import { PageLayout } from '../components/PageLayout';
import { useMessage } from '../utils/message';

const { Title, Text, Paragraph } = Typography;

const isValidWbToken = (token: string): boolean => {
  const JWT_REGEX = /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/;
  return JWT_REGEX.test(token);
};

export const Settings: React.FC = () => {
  const message = useMessage();
  const dispatch = useAppDispatch();
  const { currentUser } = useAppSelector((state) => state.user);
  const [wbToken, setWbToken] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  if (!currentUser) return null;

  const handleSaveToken = async () => {
    const trimmedToken = wbToken.trim();
    if (!trimmedToken) {
      return void message.error('Пожалуйста, введите токен');
    }

    setIsSaving(true);
    try {
      await dispatch(saveWbToken(trimmedToken)).unwrap();
      void message.success('Токен успешно сохранен');
      setWbToken('');
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        void message.error((error as { message: string }).message);
      } else {
        void message.error('Произошла неизвестная ошибка');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const saveToken = () => {
    void handleSaveToken();
  };

  const handleDeleteToken = async () => {
    try {
      await dispatch(deleteWbToken()).unwrap();
      void message.success('Token deleted successfully');
    } catch (error) {
      void message.error('Failed to delete token');
    }
  };

  const deleteToken = () => void handleDeleteToken();

  const renderWbTokenSection = () => {
    if (currentUser.wb_api_token) {
      return (
        <Card
          size="small"
          title={<Text strong>Токен Wildberries API</Text>}
          style={{ marginTop: 16 }}
          extra={<Button type="text" danger icon={<DeleteOutlined />} onClick={deleteToken} />}
        >
          <Paragraph
            copyable={{
              tooltips: ['Копировать', 'Скопировано!'],
            }}
          >
            {currentUser.wb_api_token}
          </Paragraph>
        </Card>
      );
    }

    const trimmedToken = wbToken.trim();
    const isValid = !trimmedToken || isValidWbToken(trimmedToken);

    return (
      <Card
        size="small"
        title={<Text strong>Установить токен Wildberries API</Text>}
        style={{ marginTop: 16 }}
      >
        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder="Введите токен Wildberries API"
            value={wbToken}
            onChange={(e) => setWbToken(e.target.value)}
            disabled={isSaving}
            status={trimmedToken && !isValid ? 'error' : undefined}
          />
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={saveToken}
            loading={isSaving}
            disabled={!isValid}
          >
            Сохранить
          </Button>
        </Space.Compact>
        {trimmedToken && !isValid && (
          <Text type="danger" style={{ display: 'block', marginTop: 8 }}>
            Пожалуйста, введите корректный токен Wildberries API
          </Text>
        )}
      </Card>
    );
  };

  return (
    <PageLayout>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <Avatar size={64} icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
        <Title level={3} style={{ marginTop: 16, marginBottom: 0 }}>
          {currentUser.first_name} {currentUser.last_name}
        </Title>
        {currentUser.username && <Text type="secondary">@{currentUser.username}</Text>}
      </div>

      <Descriptions bordered column={{ xs: 1, sm: 2 }} layout="vertical">
        <Descriptions.Item label="Telegram ID">{currentUser.telegramId}</Descriptions.Item>
        <Descriptions.Item label="Имя пользователя">
          {currentUser.username || 'Не указано'}
        </Descriptions.Item>
        <Descriptions.Item label="Премиум статус">
          {currentUser.is_premium ? (
            <Tag color="gold" icon={<CheckCircleOutlined />}>
              Премиум
            </Tag>
          ) : (
            <Tag color="default" icon={<CloseCircleOutlined />}>
              Стандарт
            </Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Токен Wildberries API">
          {currentUser.wb_api_token ? (
            <Tag color="green">Настроен</Tag>
          ) : (
            <Tag color="red">Не настроен</Tag>
          )}
        </Descriptions.Item>
      </Descriptions>

      {renderWbTokenSection()}

      {!currentUser.wb_api_token && (
        <Card style={{ marginTop: 16, textAlign: 'center' }}>
          <Title level={4}>Информация</Title>
          <Paragraph>
            После настройки токена Wildberries API вы сможете управлять товарами на странице
            &ldquo;Товары&rdquo;.
          </Paragraph>
        </Card>
      )}
    </PageLayout>
  );
};
